// chat/photo.tsx — the "add a photo" button + attached-photo chip for a check-in PROOF PHOTO.
//
// Cloned from oriora_plant_diary/src/chat/photo.tsx (the fleet reference). The button opens the
// native "Take Photo / Photo Library" sheet on iOS and the file picker on desktop; the image is
// downscaled ON-DEVICE (readDownscaledPhoto) and handed back as a JPEG data-URL. Nothing is ever
// uploaded — the photo lives inline in the local check-in row. This costs NOTHING: no billed brain
// call is involved in attaching a photo.

import { useRef } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import { Camera } from "lucide-react";
import { readDownscaledPhoto } from "../photo";

/** Compact icon button, sized to sit inline with the check-in form's other controls. */
const ICON_BTN: CSSProperties = {
  flexShrink: 0, width: 40, height: 40, borderRadius: 999, padding: 0, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "1px solid var(--border, #d9d4ea)", background: "var(--elev-2, #ffffff)",
  color: "var(--ink-1, #1f1b2e)", fontSize: 18, lineHeight: 1,
  transition: "background .15s, color .15s, box-shadow .15s, border-color .15s",
};

export function PhotoButton({ onPhoto, disabled }: { onPhoto: (dataUrl: string) => void; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);

  async function onPicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked twice in a row
    if (!file) return;
    try { onPhoto(await readDownscaledPhoto(file)); } catch { /* nothing changes on failure */ }
  }

  return (
    <>
      {/* accept=image/* gives iOS the native "Take Photo / Photo Library" sheet — one button, both. */}
      <input ref={ref} type="file" accept="image/*" onChange={(e) => void onPicked(e)} style={{ display: "none" }} />
      <button type="button" style={ICON_BTN} aria-label="Add a photo" title="Take or choose a photo"
        disabled={disabled} onClick={() => ref.current?.click()}>
        <Camera size={18} />
      </button>
    </>
  );
}

/** The little "photo attached" strip shown in the check-in form, so the photo is never invisible. */
export function PhotoChip({ src, onClear }: { src: string; onClear: () => void }) {
  return (
    <div className="row" style={{ gap: "0.5rem", alignItems: "center", padding: "0.4rem 0" }}>
      <img src={src} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "var(--radius-sm, 8px)" }} />
      <span className="muted" style={{ fontSize: "var(--fs-xs)", flex: 1 }}>Photo attached to this check-in.</span>
      <button type="button" className="link" style={{ fontSize: "var(--fs-xs)" }} onClick={onClear}>Remove</button>
    </div>
  );
}
